import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type { CompactionMarker } from './chatTypes';
import { formatTokens } from './ContextMeter';

/**
 * Summary sections as small labels — full-size markdown headings swamp the
 * narrow chat drawer.
 */
const SectionHeading = ({ children }: { children?: React.ReactNode }) => (
  <div className="fw-semibold mt-2">{children}</div>
);
const summaryComponents: Components = {
  h1: SectionHeading,
  h2: SectionHeading,
  h3: SectionHeading,
};

/**
 * Divider shown where a chat was compacted: the earlier turns above it were
 * summarized into the model's context (they stay visible here). Expands to the
 * summary the agent now works from.
 */
const CompactionDivider: React.FC<{ marker: CompactionMarker }> = ({
  marker,
}) => {
  const [open, setOpen] = useState(false);
  const how =
    marker.reason === 'manual'
      ? 'Compacted on request'
      : marker.reason === 'overflow'
        ? 'Compacted to fit the model'
        : 'Earlier conversation summarized';
  return (
    <div className="my-3" data-testid="chat-compaction-divider">
      <div className="d-flex align-items-center text-muted small gap-2">
        <hr className="my-0 flex-grow-1" />
        <button
          type="button"
          className="btn btn-link btn-sm text-muted text-decoration-none p-0"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {how} · {formatTokens(marker.tokens_before)} →{' '}
          {formatTokens(marker.tokens_after)} tokens · {open ? 'Hide' : 'View'}{' '}
          summary
        </button>
        <hr className="my-0 flex-grow-1" />
      </div>
      {open && (
        <div
          className="small mt-2 rounded border p-2"
          data-testid="chat-compaction-summary"
        >
          <p className="text-muted mb-2">
            The agent no longer sees the messages above this line word for word;
            it works from this summary of them. They remain here for you.
          </p>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={summaryComponents}
          >
            {marker.summary_text}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default CompactionDivider;
